import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Sidebar,
  BaseButton,
  Checkbox,
  Input,
} from "@velocity-ehs/component-library";
import classes from "./QualifiersSidebar.module.scss";
import { AvailableQualifier, EhsPermission } from "../../EhsRoleTypes";

interface IQualifiersSidebar {
  currentPermission: EhsPermission;
  selected: EhsPermission[];
  visible: boolean;
  onHide: (
    formValues: object,
    availableQualifiers: AvailableQualifier[]
  ) => void;
}

const QualifiersSidebar = ({
  visible,
  onHide,
  selected,
  currentPermission,
}: IQualifiersSidebar) => {
  const { t } = useTranslation();

  const { control, reset, watch } = useForm<object>();

  const formValues = watch();

  useEffect(() => {
    if (!currentPermission) return;
    const perm = selected.find((p) => p.id === currentPermission.id);
    const qualifiersObject = getQualifiersFormObject(perm?.assignedQualifiers);
    reset({
      ...currentPermission,
      [currentPermission.id]: qualifiersObject,
    });

    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selected, currentPermission]);

  const footerButtons = () => {
    return (
      <>
        <BaseButton
          label={t("action_okay")}
          className="p-button-text"
          onClick={handleOnHide}
        />
      </>
    );
  };

  const handleOnHide = useCallback(() => {
    onHide(formValues, currentPermission?.availableQualifiers);
  }, [formValues, currentPermission, onHide]);

  return (
    <Sidebar
      header={t("roles_edit_states")}
      visible={visible}
      onHide={handleOnHide}
      footer={footerButtons()}
    >
      <form id="qualifiers-form">
        <Input
          control={control}
          label={t("universal_permission_name")}
          name="name"
          readOnly={true}
        />
        <Input
          control={control}
          label={t("universal_capability_title")}
          name="capability"
          readOnly={true}
        />
        <p className={classes.subtitle}>{t("universal_states")}</p>
        {currentPermission?.availableQualifiers?.map((qualifier) => (
          <Checkbox
            key={qualifier.id}
            control={control}
            label={qualifier.name}
            name={`${currentPermission?.id}.${qualifier.id}`}
          />
        ))}
      </form>
    </Sidebar>
  );
};

const getQualifiersIds = (qualifiers: Array<{ id: string; name: string }>) =>
  qualifiers?.map((qualifier) => qualifier.id);

const getQualifiersFormObject = (
  qualifiers: Array<{ id: string; name: string }>
) => {
  const qualifiersIds = getQualifiersIds(qualifiers);
  return qualifiersIds?.reduce((acc, value) => ({ ...acc, [value]: true }), {});
};

export { QualifiersSidebar };
